/**
 * PATH: apps/hr/src/pages/documents/DocumentsPage.tsx
 * PURPOSE: Employee documents management with OCR status tracking
 * WHY: Document lifecycle management for HR — upload, review, approve, track expiry
 *
 * FLOW:
 *   useDocumentsData (fetch/filter) → Stats → Filters → Table → Modals
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FileText,
  Search,
  Clock,
  AlertTriangle,
  XCircle,
  Upload,
  Filter,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import type { DocumentStatus, DocumentType } from '../../types'
import { useDocumentsData } from './documents-list/useDocumentsData'
import { StatCard } from './documents-list/StatusBadges'
import { UploadModal } from './documents-list/UploadModal'
import { DocumentRow, DeleteModal } from './documents-list/DocumentRow'
import { hrApi } from '../../lib/setupApi'

export default function DocumentsPage() {
  const navigate = useNavigate()
  const data = useDocumentsData()
  
  const [showUploadModal, setShowUploadModal] = useState(data.shouldShowUploadOnMount)
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null)
  const [deleteWorkerId, setDeleteWorkerId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleUpload = async (files: File[], employeeId: string, docType: string) => {
    for (const file of files) { await hrApi.uploadEmployeeDocument(employeeId, file, docType) }
    await data.refetch()
    data.queryClient.invalidateQueries({ queryKey: ['all-documents'] })
  }

  const handleDelete = async () => {
    if (!deleteDocId || !deleteWorkerId) return
    setIsDeleting(true)
    try {
      await hrApi.deleteDocument(deleteWorkerId, deleteDocId)
      setDeleteDocId(null); setDeleteWorkerId(null)
      await data.refetch()
    } catch (error) { console.error('Delete failed:', error) }
    finally { setIsDeleting(false) }
  }

  const { stats, filteredDocuments, currentPage, setCurrentPage } = data
  const totalPages = data.pagination.total_pages

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Documents</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Manage employee documents, licenses, and certifications</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/documents/review')} className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
            <AlertTriangle className="w-4 h-4 text-amber-500" />Review Queue ({stats.needsReview})
          </button>
          <button onClick={() => setShowUploadModal(true)} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">
            <Upload className="w-4 h-4" />Upload Document
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Total Documents" value={stats.total} icon={FileText} color="bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400" onClick={() => data.setStatusFilter('all')} isActive={data.statusFilter === 'all'} />
        <StatCard label="Expiring Soon" value={stats.expiring30Days} icon={Clock} color="bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400" onClick={() => data.setExpiryFilter('30days')} isActive={data.expiryFilter === '30days'} />
        <StatCard label="Expired" value={stats.expired} icon={XCircle} color="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400" onClick={() => data.setStatusFilter('expired')} isActive={data.statusFilter === 'expired'} />
        <StatCard label="Needs Review" value={stats.needsReview} icon={AlertTriangle} color="bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400" onClick={() => data.setStatusFilter('needs_review')} isActive={data.statusFilter === 'needs_review'} />
        <StatCard label="Processing" value={stats.pendingOCR} icon={RefreshCw} color="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" onClick={() => data.setStatusFilter('pending_ocr')} isActive={data.statusFilter === 'pending_ocr'} />
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search by document name, number, or employee..." value={data.search} onChange={(e) => { data.setSearch(e.target.value); setCurrentPage(1) }} className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => data.setShowFilters(!data.showFilters)} className={cn("flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors", data.showFilters ? "bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600" : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800")}>
              <Filter className="w-4 h-4" />Filters
              {data.hasActiveFilters && <span className="px-1.5 py-0.5 text-xs rounded-full bg-teal-500 text-white">{[data.statusFilter !== 'all', data.typeFilter !== 'all', data.expiryFilter].filter(Boolean).length}</span>}
            </button>
            <button className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"><Download className="w-4 h-4" />Export</button>
          </div>
        </div>
        {data.showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Status</label>
                <select value={data.statusFilter} onChange={(e) => { data.setStatusFilter(e.target.value as any); setCurrentPage(1) }} className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm">
                  <option value="all">All Statuses</option><option value="pending_ocr">Processing</option><option value="needs_review">Needs Review</option><option value="approved">Approved</option><option value="rejected">Rejected</option><option value="expired">Expired</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Document Type</label>
                <select value={data.typeFilter} onChange={(e) => { data.setTypeFilter(e.target.value as any); setCurrentPage(1) }} className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm">
                  <option value="all">All Types</option><option value="license">License</option><option value="certification">Certification</option><option value="contract">Contract</option><option value="identification">Identification</option><option value="visa">Visa</option><option value="medical">Medical</option><option value="training">Training</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Expiry Window</label>
                <select value={data.expiryFilter} onChange={(e) => { data.setExpiryFilter(e.target.value); setCurrentPage(1) }} className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm">
                  <option value="">All</option><option value="expired">Expired</option><option value="7days">Expiring in 7 days</option><option value="30days">Expiring in 30 days</option><option value="60days">Expiring in 60 days</option>
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={data.handleClearFilters} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">Clear Filters</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Documents Table */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {data.isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /><span className="ml-3 text-slate-500">Loading documents...</span></div>
        ) : filteredDocuments.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                    {['Document', 'Type', 'Employee', 'Confidence', 'Expiry', 'Days', 'Status', ''].map(h => (
                      <th key={h} className={cn("text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4", h === '' ? 'text-right' : 'text-left')}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map(doc => (
                    <DocumentRow key={doc.id} doc={doc} onClick={() => navigate(`/documents/${doc.id}`)} onDelete={(e) => { e.stopPropagation(); setDeleteDocId(doc.id); setDeleteWorkerId(doc.employeeId) }} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-800">
              <p className="text-sm text-slate-500 dark:text-slate-400">Showing {filteredDocuments.length} of {filteredDocuments.length} documents</p>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page: number
                    if (totalPages <= 5) page = i + 1
                    else if (currentPage <= 3) page = i + 1
                    else if (currentPage >= totalPages - 2) page = totalPages - 4 + i
                    else page = currentPage - 2 + i
                    return (
                      <button key={page} onClick={() => setCurrentPage(page)} className={cn('min-w-[32px] h-8 text-sm rounded-lg transition-colors', page === currentPage ? 'bg-teal-600 text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400')}>{page}</button>
                    )
                  })}
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="py-16 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-700 mb-3" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-1">No documents found</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-4">{data.hasActiveFilters ? 'Try adjusting your filters' : 'Upload your first document to get started'}</p>
            {!data.hasActiveFilters && (
              <button onClick={() => setShowUploadModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"><Upload className="w-4 h-4" />Upload Your First Document</button>
            )}
          </div>
        )}
      </div>

      <UploadModal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} employees={data.employees} onUpload={handleUpload} />
      <DeleteModal isOpen={!!deleteDocId} onClose={() => { setDeleteDocId(null); setDeleteWorkerId(null) }} onConfirm={handleDelete} isDeleting={isDeleting} />
    </div>
  )
}
